<template>
  <card>
    <FullCalendar :options="calendarOptions" />
    <modal :show.sync="isEventModalVisible">
      <h4 slot="header" class="title title-up">
        {{ $t("message.rosterScheduleEvent") }}
      </h4>
      <ValidationObserver ref="eventFormValidator" v-slot="{ handleSubmit }">
        <b-form
          id="event-form"
          @submit.stop.prevent="handleSubmit(() => onSaveEvent(event))"
        >
          <ValidationProvider
            rules="required"
            v-slot="{ dirty, validated, valid, errors }"
          >
            <fg-input :label="$t('message.employee')" :error="errors[0]">
              <b-form-select
                v-model="event.resourceId"
                :options="employeeOptions"
                :state="dirty || validated ? valid : null"
              />
            </fg-input>
          </ValidationProvider>
          <fg-input :label="$t('message.rosterEvent')">
            <b-form-select v-model="event.type" :options="eventTypeOptions" />
          </fg-input>
          <fg-input :label="$t('message.rosterEventFrom')">
            <vue-ctk-date-time-picker
              format="YYYY-MM-DDTHH:mm:ss"
              formatted="dddd, DD MMMM YYYY hh:mm A"
              v-model="event.start"
              :minute-interval="15"
              :no-label="true"
              required
            />
          </fg-input>
          <fg-input :label="$t('message.rosterEventTo')">
            <vue-ctk-date-time-picker
              format="YYYY-MM-DDTHH:mm:ss"
              formatted="dddd, DD MMMM YYYY hh:mm A"
              v-model="event.end"
              :minute-interval="15"
              :no-label="true"
              required
            />
          </fg-input>
        </b-form>
      </ValidationObserver>
      <div slot="footer" class="w-100 d-flex justify-content-between">
        <div>
          <n-button
            v-if="event._id"
            type="danger"
            :busy="isDeleting"
            @click.native.prevent="onDeleteEvent(event)"
          >
            {{ $t("message.delete") }}
          </n-button>
        </div>
        <div>
          <n-button
            class="mr-1"
            type="secondary"
            simple
            @click.native="hideEventModal"
          >
            {{ $t("message.cancel") }}
          </n-button>
          <n-button
            type="primary"
            native-type="submit"
            form="event-form"
            :busy="isSaving"
          >
            {{ $t("message.save") }}
          </n-button>
        </div>
      </div>
    </modal>
  </card>
</template>

<script>
import _ from "lodash";
import moment from "moment";
import { mapGetters, mapActions } from "vuex";
import FullCalendar from "@fullcalendar/vue";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import { ValidationObserver, ValidationProvider } from "vee-validate";

import {
  DEFAULT_ROSTER_EVENT,
  rosterEventTypes
} from "@/store/management/roster";
import { forceViewValid, forceDateValid } from "@/calendars";
import { Modal } from "@/components";
import momentTimezonePlugin from "@fullcalendar/moment-timezone";
import { calendarRouteDefaults, standardizeRosterTime } from "@/helpers/rosterTime";

const today = moment().tz("GMT");

export default {
  components: {
    FullCalendar,
    ValidationObserver,
    ValidationProvider,
    Modal
  },
  data() {
    const { view, date } = calendarRouteDefaults(
      this.$route.params,
      moment().format("YYYY-MM-DD")
    );
    const initialView = view;
    const initialDate = date;
    return {
      calendarOptions: {
        plugins: [dayGridPlugin, timeGridPlugin, interactionPlugin, momentTimezonePlugin],
        headerToolbar: {
          start: "prev,next today",
          center: "title",
          end: "timeGridDay,timeGridWeek,dayGridMonth"
        },
        initialView,
        initialDate,
        timeZone : "GMT",
        scrollTime: today.format("HH"),
        slotDuration: "00:15:00",
        now: moment().utc(true).format("YYYY-MM-DDTHH:mm:ss"),
        displayEventTime: false,
        slotLabelInterval: "01:00",
        nowIndicator: true,
        selectable: true,
        droppable: true,
        editable: true,
        events: [],
        datesSet: this.onDatesRender,
        select: this.onSelect,
        eventClick: ({ event }) => this.onEventClick(event),
        eventDrop: ({ event }) => this.onEventDrop(event),
        eventResize: ({ event }) => this.onEventResize(event)
      },

      event: _.cloneDeep(DEFAULT_ROSTER_EVENT),
      eventTypeOptions: [
        {
          value: rosterEventTypes.RESOURCE_WORK_HOURS,
          text: this.$t("message.rosterEmployeeWorkHours")
        },
        {
          value: rosterEventTypes.RESOURCE_BREAK,
          text: this.$t("message.rosterEmployeeBreak")
        },
        {
          value: rosterEventTypes.RESOURCE_HOLIDAY,
          text: this.$t("message.rosterEmployeeHoliday")
        },
        {
          value: rosterEventTypes.RESOURCE_SICK,
          text: this.$t("message.rosterEmployeeSick")
        }
      ],
      isEventModalVisible: false,
      isSaving: false,
      isDeleting: false
    };
  },
  methods: {
    ...mapActions({
      fetchEmployees: "employees/fetchEmployees",
      subscribeRoster: "roster/subscribeRoster",
      unsubscribeRoster: "roster/unsubscribeRoster",
      createEvent: "roster/createEvent",
      updateEvent: "roster/updateEvent",
      deleteEvent: "roster/deleteEvent"
    }),
    isMidnight(date) {
      return moment(date).format("HH:mm") === "00:00";
    },
    isAllDay({ start, end }) {
      return this.isMidnight(start) && this.isMidnight(end);
    },
    startEndExists(start,end){
      return typeof(end) === "string" && typeof(start) === "string"
    },
    standardizeTime(time){
      return standardizeRosterTime(time);
    }
    ,
    sanitizeEvent(event) {
      let { start, end } = event;

      end =
        end ||
        moment(start)
          .add(1, "day")
          .format();
      // To Explain how this works if they've already been created, convert local to utc without changing the time else convert to utc and like roster.js handle it
      if ( this.startEndExists(start,end)){
      return {
        ...event,
        start: this.standardizeTime(start),
        end: this.standardizeTime(end),
        allDay: this.isAllDay(event)
      }} else  {
        return {
          ...event,
          start: moment(start).utc().format(),
          end: moment(end).utc().format(),
          allDay: this.isAllDay(event)
      };
    }
    },
    onSelect({ start, end }) {
      if (!end) end = moment(start).add(30, "minutes");

      let event = {
        ...DEFAULT_ROSTER_EVENT,
        start,
        end
      };
      event = this.sanitizeEvent(event);

      this.setEvent(event);
      this.resetEventFormValidation();
      this.showEventModal();
    },
    onEventClick({ id }) {
      let event = this.roster[id];
      event = this.sanitizeEvent(event);

      this.setEvent(event);
      this.showEventModal();
    },
    onEventDrop(event) {
      this.onEventMove(event);
    },
    onEventResize(event) {
      this.onEventMove(event);
    },
    onEventMove({ id, start, end }) {
      let event = {
        ...this.roster[id],
        start,
        end
      };

      event = this.sanitizeEvent(event);

      this.onSaveEvent(event);
    },
    setEvent(event) {
      this.event = event;
    },
    async onSaveEvent(event) {
      this.showSavingIndicator();
      event = this.sanitizeEvent(event);

      try {
        if (event._id) {
          await this.updateEvent(event);
        } else {
          await this.createEvent(event);
        }
      } catch ({ message }) {
        this.$notify({
          type: "danger",
          title: this.$t("message.error"),
          text: message
        });
      } finally {
        this.hideSavingIndicator();
        this.hideEventModal();
      }
    },
    async onDeleteEvent(event) {
      this.showDeletingIndicator();

      try {
        await this.deleteEvent(event);
      } catch ({ message }) {
        this.$notify({
          type: "danger",
          title: this.$t("message.error"),
          text: message
        });
      } finally {
        this.hideDeletingIndicator();
        this.hideEventModal();
      }
    },
    showSavingIndicator() {
      this.isSaving = true;
    },
    hideSavingIndicator() {
      this.isSaving = false;
    },
    showDeletingIndicator() {
      this.isDeleting = true;
    },
    hideDeletingIndicator() {
      this.isDeleting = false;
    },
    showEventModal() {
      this.isEventModalVisible = true;
    },
    hideEventModal() {
      this.isEventModalVisible = false;
    },
    resetEventFormValidation() {
      this.$refs.eventFormValidator.reset();
    },
    getEventColor({ type }) {
      return {
        [rosterEventTypes.RESOURCE_WORK_HOURS]: "blue",
        [rosterEventTypes.RESOURCE_BREAK]: "orange",
        [rosterEventTypes.RESOURCE_HOLIDAY]: "orange",
        [rosterEventTypes.RESOURCE_SICK]: "red"
      }[type];
    },
    getEventTitle({ resourceId, type, start, end }) {
      const employee = this.employees[resourceId];

      if (!employee) return "";

      let title = `${moment(start).tz("UTC").format("HH:mm")} - ${moment(end).tz("UTC").format("HH:mm")} ${employee.name}`;

      if (type === rosterEventTypes.RESOURCE_HOLIDAY) {
        title += ` (${this.$t("message.rosterEmployeeHoliday")})`;
      } else if (type === rosterEventTypes.RESOURCE_SICK) {
        title += ` (${this.$t("message.rosterEmployeeSick")})`;
      } else if (type === rosterEventTypes.RESOURCE_BREAK) {
        title += ` (${this.$t("message.rosterEmployeeBreak")})`;
      }

      return title;
    },
    onDatesRender({ view }) {
      const { type, currentStart } = view;
      const date = moment(currentStart).format("YYYY-MM-DD");

      this.updateRouteParams({ view: type, date });
    },
    updateRouteParams({ view, date }) {
      const url = this.$route.path.replace(
        /calendar.*/,
        `calendar/${view}/${date}`
      );

      window.history.replaceState(null, "", url);
    }
  },
  computed: {
    ...mapGetters({
      roster: "roster/roster",
      employees: "employees/employees",
      employeesList: "employees/employeesList"
    }),
    calendarEvents() {
      return Object.values(this.roster).map(event => {
        return {
          ...event,
          id: event._id,
          title: this.getEventTitle(event),
          display :'block',
          backgroundColor: this.getEventColor(event),
          textColor: "white",
          borderColor: "transparent"
        };
      });
    },
    employeeOptions() {
      return [
        {
          value: null,
          text: this.$t("message.selectAnEmployee"),
          disabled: true
        },
        ...this.employeesList.map(employee => ({
          value: employee._id,
          text: employee.name
        }))
      ];
    }
  },
  async created() {
    try {
      this.fetchEmployees();
      this.subscribeRoster();
    } catch ({ message }) {
      this.$notify({
        type: "danger",
        title: this.$t("message.error"),
        text: message
      });
    }
  },
  beforeDestroy() {
    this.unsubscribeRoster();
  },
  watch: {
    calendarEvents: {
      deep: true,
      handler(value) {
        this.calendarOptions.events = value;
      }
    }
  }
};
</script>
